'use client';

import first from 'lodash/first';
import { type RefObject, useCallback, useMemo, useRef } from 'react';
import {
  OZONE_TEL_HANDLER_CALLBACK_TYPES,
  OZT_CALLBACK_TYPES,
  type OztCallback,
  type OztCallbackType,
  toOztCallback,
} from '../../consts/ozonetel/callbacks';
import {
  type DigitalConsoleType,
  OZONE_TEL_HANDLER_METHODS,
  OZONE_TEL_SIP_LIFECYCLE_METHODS,
  type OzonetelCallStatus,
  type OzonetelHandlerName,
  type OzonetelInitializePayload,
  type OzonetelSipLifecycleMethod,
  type OzonetelWindow,
} from '../../consts/ozonetel/sdk';
import type { CustomOztHandlerName } from '../../consts/ozonetel/shared';
import { getCallRecordingData } from '../../utils/getCallRecordingData';
import { getOzonetelSdk } from '../../utils/ozonetel/getSdk';
import { loadOzonetelSdk } from '../../utils/ozonetel/loadSdk';
import { isOzonetelSessionAlive, reconnectOzonetelSession } from '../../utils/ozonetel/readiness';

/**
 * A utility function that registers one SDK callback. The SIP ones live on the
 * prototype and reach for private fields, so an extracted-then-called reference throws; the
 * call/drop registrars only survive that because the SDK builds those as per-instance arrows.
 *
 * A registrar that blows up must not take the agent login down with it, so failures are contained
 * here rather than bubbling out through the binding loop.
 */
function registerSdkCallback(
  dc: DigitalConsoleType,
  methodName: keyof DigitalConsoleType,
  callback: (payload: unknown) => unknown,
) {
  const registerHandler = dc[methodName];
  if (typeof registerHandler !== 'function') return false;

  try {
    void Promise.resolve(
      (registerHandler as (handler: typeof callback) => unknown).call(dc, callback),
    ).catch((error) => console.error(`Failed to bind Ozonetel handler: ${methodName}`, error));
  } catch (error) {
    console.error(`Failed to bind Ozonetel handler: ${methodName}`, error);
    return false;
  }

  return true;
}

interface UseOzonetelSdkLifecycleParams {
  callStatusRef: RefObject<OzonetelCallStatus>;
  isInitializedRef: RefObject<boolean>;
  setCallStatus: (status: OzonetelCallStatus) => void;
  publishOztCallback: (eventName: 'onOztCallback', event: OztCallback) => void;
  releaseAgentAudio: () => void;
  markSipReady: () => void;
  markSipLost: () => void;
}

export function useOzonetelSdkLifecycle({
  callStatusRef: _callStatusRef,
  isInitializedRef,
  setCallStatus,
  publishOztCallback,
  releaseAgentAudio,
  markSipReady,
  markSipLost,
}: UseOzonetelSdkLifecycleParams) {
  const pendingPayloadRef = useRef<OzonetelInitializePayload | undefined>(undefined);

  const emitOztCallback = useCallback(
    (handlerName: OzonetelHandlerName | CustomOztHandlerName, payload: unknown) => {
      switch (handlerName) {
        case 'onDropCall':
        case 'onAgentRelease':
          releaseAgentAudio();
          setCallStatus('IDLE');
          break;
        case 'onAgentAnswered':
        case 'onNewCall':
          setCallStatus('IN_CALL');
          break;
        default:
          break;
      }

      // SDK publishes integration events regardless of mounted page state.
      publishOztCallback(
        'onOztCallback',
        toOztCallback(OZONE_TEL_HANDLER_CALLBACK_TYPES[handlerName], payload),
      );
    },
    [publishOztCallback, releaseAgentAudio, setCallStatus],
  );

  const customHandlers = useMemo(() => {
    const pollCallRecording = (dc: ReturnType<typeof getOzonetelSdk>) => {
      if (!dc) return;
      const interval = setInterval(async () => {
        const callHistory = await getCallRecordingData(dc);
        const firstCall = first(callHistory);
        if (firstCall?.Status === 'Fail') {
          clearInterval(interval);
          return;
        }
        if (firstCall?.audioFile) {
          emitOztCallback('onCallRecording', firstCall);
          clearInterval(interval);
        }
      }, 5000);
    };

    const handleWithRecordingFetch =
      (handlerName: 'onDropCall' | 'onAgentRelease') => (payload: unknown) => {
        const dc = getOzonetelSdk();
        if (!dc) {
          console.error('Cannot obtain data without initialized SDK');
          return;
        }
        emitOztCallback(handlerName, payload);
        pollCallRecording(dc);
      };

    return {
      onDropCall: handleWithRecordingFetch('onDropCall'),
      onAgentRelease: handleWithRecordingFetch('onAgentRelease'),
    };
  }, [emitOztCallback]);

  const bindSipLifecycleHandlers = useCallback(() => {
    const ozonetelWindow = window as OzonetelWindow;
    const dc = getOzonetelSdk();
    if (!dc) return;

    const publishSipStatus = (eventType: OztCallbackType) => (payload: unknown) => {
      publishOztCallback('onOztCallback', toOztCallback(eventType, payload));
    };

    const onSipRegistered = publishSipStatus(OZT_CALLBACK_TYPES.onSipRegistered);
    const onSipDisconnected = publishSipStatus(OZT_CALLBACK_TYPES.onSipDisconnected);

    const sipHandlers = {
      onSIPRegistered: (payload: unknown) => {
        markSipReady();
        onSipRegistered(payload);
      },
      onSIPDisconnected: (payload: unknown) => {
        markSipLost();
        onSipDisconnected(payload);
      },
      onSIPRegistrationFailed: (payload: unknown) => {
        markSipLost();
        onSipDisconnected(payload);
      },
    } satisfies Record<OzonetelSipLifecycleMethod, (payload: unknown) => void>;

    const bindings = ozonetelWindow.__UA_OZONETEL_SIP_BINDINGS__ ?? {};

    for (const methodName of OZONE_TEL_SIP_LIFECYCLE_METHODS) {
      if (bindings[methodName]) continue;
      if (registerSdkCallback(dc, methodName, sipHandlers[methodName])) {
        bindings[methodName] = true;
      }
    }

    ozonetelWindow.__UA_OZONETEL_SIP_BINDINGS__ = bindings;
  }, [markSipLost, markSipReady, publishOztCallback]);

  const bindOzonetelHandlers = useCallback(() => {
    const ozonetelWindow = window as OzonetelWindow;
    const dc = getOzonetelSdk();
    if (!dc) return;

    bindSipLifecycleHandlers();

    const bindings = ozonetelWindow.__UA_OZONETEL_EVENT_BINDINGS__ ?? {};
    const handlerEntries = Object.entries(OZONE_TEL_HANDLER_METHODS) as [
      OzonetelHandlerName,
      (typeof OZONE_TEL_HANDLER_METHODS)[OzonetelHandlerName],
    ][];

    for (const [handlerName, methodName] of handlerEntries) {
      if (bindings[handlerName]) continue;

      const handler = (p: unknown) =>
        customHandlers[handlerName]
          ? customHandlers[handlerName](p)
          : emitOztCallback(handlerName, p);

      if (registerSdkCallback(dc, methodName, handler)) {
        bindings[handlerName] = true;
      }
    }

    ozonetelWindow.__UA_OZONETEL_EVENT_BINDINGS__ = bindings;
  }, [bindSipLifecycleHandlers, emitOztCallback, customHandlers]);

  const initializeOzonetel = useCallback(
    async (initializePayload?: OzonetelInitializePayload) => {
      const payload = initializePayload ?? pendingPayloadRef.current;
      if (!payload) return;

      pendingPayloadRef.current = payload;

      try {
        await loadOzonetelSdk();
        const dc = getOzonetelSdk();
        if (!dc?.initialize) {
          console.error('Ozonetel SDK did not expose initialize');
          return;
        }

        // bind before logging in, otherwise the softphone can register before anything is listening
        // and the very first call has nothing to wait on
        bindOzonetelHandlers();

        if (isInitializedRef.current) {
          if (isOzonetelSessionAlive(dc)) return;

          // if tab switch caused disconnection, then perform cheap reconnect instead of full login
          if (await reconnectOzonetelSession(dc)) return;

          isInitializedRef.current = false;
          setCallStatus('UNAUTHORIZED');
        }

        // mark the softphone for previous session as not ready
        markSipLost();

        // try to logout if already initialized
        try {
          await dc.logout?.(payload.customer, payload.agentid);
        } catch {
          // ignore error
        }

        // login to the agent
        const response = (await dc.initialize(payload)) as
          | { success: true }
          | { status: 'error'; error: string };

        // if successful, update the state
        if ('success' in response && response.success) {
          isInitializedRef.current = true;
          setCallStatus('IDLE');
        } else {
          throw new Error(
            `Failed to initialize Ozonetel SDK: ${
              'message' in response ? response.message : 'Unknown error'
            }`,
          );
        }
      } catch (error) {
        console.error('Failed to load Ozonetel SDK', error);
      }
    },
    [bindOzonetelHandlers, isInitializedRef, markSipLost, setCallStatus],
  );

  const getInitializedSdk = useCallback(async () => {
    if (!isInitializedRef.current && !pendingPayloadRef.current) {
      throw new Error('Ozonetel initialize payload is not available');
    }
    await initializeOzonetel();
    const dc = getOzonetelSdk();
    if (!dc) {
      throw new Error('Ozonetel SDK is not available');
    }
    return dc;
  }, [initializeOzonetel, isInitializedRef]);

  return { pendingPayloadRef, initializeOzonetel, getInitializedSdk } as const;
}
